import { Inject } from '@odinjs/odin';
import { FetchAPI, ModalAPI, ModalService } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClose, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AuthenticationService, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ResultTermServiceAPI } from '@philips-internal/pending-approval-components';

import { FoundationRoute } from '../../constants';
import { Endpoint } from '../../constants/endpoints';
import BaseController from '../BaseController';

@Controller({ fragment: 'assign-terms', component: 'assign-terms-modal' })
export default class AssignTermsController extends BaseController {

  @Inject('terms-fetch')
    termsFetchApi: FetchAPI;

  @Inject('result-term-service')
    checkTermServiceApi: ResultTermServiceAPI;

  termOfUse = 'TU';
  privacy = 'AP';

  @OnMount('assign-terms-modal')
  onMount(event: MountEvent): void {
    const modal = event.target as ModalAPI;
    const { termsFetchApi, checkTermServiceApi } = this;

    this.checkTermsOfUse(modal, termsFetchApi, checkTermServiceApi);
  }

  private checkTermsOfUse(modal: ModalAPI, termsFetchApi: FetchAPI, checkTermServiceApi: ResultTermServiceAPI): void {
    window.addEventListener('beforeunload', this.logout);

    HttpService.get(Endpoint.CHECK_TERMS, {
      type: this.termOfUse,
    }).then((term) => {
      if (!term.body) {
        checkTermServiceApi.setTerm('newaccount.link.terms');
        checkTermServiceApi.setCheck(true);
        this.context.set({ type: 'term' });
        modal.setTitle('newaccount.link.terms');

        termsFetchApi.setParameters({ type: this.termOfUse });
        termsFetchApi.setEndpoint(Endpoint.GET_LATEST_TERMS);
        termsFetchApi.forceFetch();
      } else {
        this.checkPrivacyNotice(modal, termsFetchApi, checkTermServiceApi);
      }
    }).catch((err) => {
      checkTermServiceApi.setTerm('newaccount.link.terms');
      checkTermServiceApi.setCheck(true);
      this.context.set({ type: 'term' });
      modal.setTitle('newaccount.link.terms');
      throw err;
    });
  }

  private checkPrivacyNotice(modal: ModalAPI, termsFetchApi: FetchAPI, checkTermServiceApi: ResultTermServiceAPI): void {
    HttpService.get(Endpoint.CHECK_TERMS, {
      type: this.privacy,
    }).then((privacy) => {
      if (!privacy.body) {
        checkTermServiceApi.setCheck(false);
        checkTermServiceApi.setTerm('newaccount.privacy');
        this.context.set({ type: 'privacy' });
        modal.setTitle('newaccount.privacy');

        termsFetchApi.setParameters({ type: this.privacy });
        termsFetchApi.setEndpoint(Endpoint.GET_LATEST_TERMS);
        termsFetchApi.forceFetch();
      } else {
        ModalService.close('assign-terms');
        applicationContext.redirect(FoundationRoute.SPOTLIGHT as string);
      }
    }).catch((err) => {
      ModalService.close('assign-terms');
      applicationContext.redirect(FoundationRoute.SPOTLIGHT as string);
      throw err;
    });
  }

  @OnMount({ target: 'result-term-service' })
  onTermo(): void {
    const { checkTermServiceApi } = this;
    checkTermServiceApi.onSubmitTermUse.listen(() => this.updateTermsOfUse());
  }

  @OnAfterFetch({ target: 'terms-fetch' })
  onAfterFetchTerms(event: FetchEvent): void {
    if (event.data) {
      this.processTermsData(event.data);
    }
  }

  private processTermsData(data: any): void {
    const termsData = data as { termDescription?: string; termSeqNr?: number };

    if (termsData.termDescription) {
      const formattedData = {
        termDescription: termsData.termDescription,
        termName: 'Terms of Use',
        termSeqNr: termsData.termSeqNr ?? 0,
        confirmationDate: '',
        status: 'pending',
      };

      this.checkTermServiceApi.setData(formattedData);
    }
  }

  @OnClose('assign-terms-modal')
  onClose(): void {
    const type = this.context.get('type') as string;

    if (type === 'term') {
      this.logout();
    } else {
      this.updatePrivacy();
    }
  }

  logout(): void {
    AuthenticationService.logout().finally(() => {
      ModalService.close('assign-terms');
      this.historyService.push('/login');
      window.location.reload();
      window.removeEventListener('beforeunload', this.logout);
    });
  }

  updateTermsOfUse(): void {
    HttpService.put(Endpoint.UPDATE_TERMS, {
      type: this.termOfUse,
    }).then(() => {
      // After accepting terms, redirect to check privacy or home
      ModalService.close('assign-terms');
      applicationContext.redirect(FoundationRoute.SPOTLIGHT as string);
    }).catch((err) => {
      throw err;
    });
  }

  updatePrivacy(): void {
    HttpService.put(Endpoint.UPDATE_TERMS, {
      type: this.privacy,
    }).then(() => {
      ModalService.close('assign-terms');
      applicationContext.redirect(FoundationRoute.SPOTLIGHT as string);
    }).catch((err) => {
      throw err;
    });
  }
}
